import { handleError } from "../../../util/errors";
import apiBase from "../../api";
import fileDownload from "js-file-download";
import {
  ADD_ITEM_TO_VENDING_CONTROLLER,
  ADD_VENDING_CONTROLLER_TO_FISCAL_DEVICE,
  BULK_REASSIGN_VENDING_CONTROLLERS,
  DELETE_ITEM,
  DOWNLOAD_VENDING_CONTROLLERS_XLS,
  DOWNLOAD_VENDING_CONTROLLERS_OTA,
  FETCH_ACTIVE_VENDING_CONTROLLERS,
  FETCH_PENDING_VENDING_CONTROLLERS,
  GET_VENDING_CONTROLLER,
  GET_VENDING_CONTROLLER_INVOICES,
  GET_VENDING_CONTROLLER_LAST_INVOICE,
  UPDATE_ITEM,
  UPDATE_VENDING_CONTROLLER,
  SYNC_VC_ATTACH_INTERVEND,
  SYNC_ITEM_UPSERT_INTERVEND,
  SYNC_ITEM_DELETE_INTERVEND,
  SYNC_ALL_ITEMS_INTERVEND,
  INTERVEND_VM_OPTIONS,
} from "../../urls";

class VendingControllersService {
  fetchPendingControllers = async (page, perPage, term) => {
    try {
      const resposne = await apiBase.get(
        FETCH_PENDING_VENDING_CONTROLLERS(page, perPage, term)
      );
      if (resposne.status !== 200) throw new Error();
      const { data } = resposne.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error fetching fiscal devices");
    }
  };

  fetchActiveControllers = async (page, perPage, term, filterOffline) => {
    try {
      const resposne = await apiBase.get(
        FETCH_ACTIVE_VENDING_CONTROLLERS(page, perPage, term, filterOffline)
      );
      if (resposne.status !== 200) throw new Error();
      const { data } = resposne.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error fetching fiscal devices");
    }
  };

  updateVendingController = async (vendingController) => {
    try {
      const response = await apiBase.put(
        UPDATE_VENDING_CONTROLLER,
        vendingController
      );
      if (response.status !== 201) throw new Error();
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error fetching fiscal devices");
    }
  };

  bulkReassignVendingControllers = async ({
    vendingControllerIds,
    fiscalDeviceId,
  }) => {
    try {
      const response = await apiBase.post(BULK_REASSIGN_VENDING_CONTROLLERS, {
        vendingControllerIds,
        fiscalDeviceId,
      });
      if (response.status !== 200) throw new Error();
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error reassigning vending controllers");
    }
  };

  addVendingControllerToFiscalDevice = async ({ id, fiscalDeviceId }) => {
    try {
      const response = await apiBase.post(
        ADD_VENDING_CONTROLLER_TO_FISCAL_DEVICE(id),
        { fiscalDeviceId }
      );
      if (response.status !== 201) throw new Error();
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(
        err,
        "Error adding vending controller to fiscal device"
      );
    }
  };

  addItemToVendingController = async ({ id, item }) => {
    try {
      const response = await apiBase.post(
        ADD_ITEM_TO_VENDING_CONTROLLER(id),
        item
      );
      if (response.status !== 201) throw new Error();
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error adding item");
    }
  };

  deleteItem = async ({ id, itemId }) => {
    try {
      const response = await apiBase.delete(DELETE_ITEM(id, itemId));
      if (response.status !== 200) throw new Error();
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error deleting item");
    }
  };

  updateItem = async ({ id, item }) => {
    try {
      const response = await apiBase.put(UPDATE_ITEM(id), { item });
      if (response.status !== 200) throw new Error();
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error updating item");
    }
  };

  // --- Push a device change to Intervend (backend enqueues + guards excluded test taxpayers).
  // The response's data.queued tells whether it was actually queued (false = skipped, with a reason).
  // vendingMachine (optional): { action: 'attach', vendingMachineId } | { action: 'create', name }
  // — tells IV which vending machine to link the controller to (or to create a new one).
  syncVcAttachToIntervend = async ({ id, vendingMachine }) => {
    try {
      const response = await apiBase.post(
        SYNC_VC_ATTACH_INTERVEND(id),
        vendingMachine ? { vendingMachine } : {}
      );
      return response.data;
    } catch (err) {
      throw handleError(err, "Error syncing attach to Intervend");
    }
  };

  // IV vending machines without a controller for this VC's company (attach-or-create picker data).
  // Returns { excluded, companyPib, suggestedName, vms: [{ id, name, uuid, businessUnit }] }.
  getIntervendVmOptions = async ({ id }) => {
    try {
      const response = await apiBase.get(INTERVEND_VM_OPTIONS(id));
      return response.data;
    } catch (err) {
      throw handleError(err, "Error fetching Intervend VM options");
    }
  };

  syncItemUpsertToIntervend = async ({ id, identifier }) => {
    try {
      const response = await apiBase.post(SYNC_ITEM_UPSERT_INTERVEND(id), { identifier });
      return response.data;
    } catch (err) {
      throw handleError(err, "Error syncing item to Intervend");
    }
  };

  syncItemDeleteToIntervend = async ({ id, identifier }) => {
    try {
      const response = await apiBase.post(SYNC_ITEM_DELETE_INTERVEND(id), { identifier });
      return response.data;
    } catch (err) {
      throw handleError(err, "Error syncing item delete to Intervend");
    }
  };

  // Sync ALL current items on the controller to Intervend (after applying an item template).
  syncAllItemsToIntervend = async ({ id }) => {
    try {
      const response = await apiBase.post(SYNC_ALL_ITEMS_INTERVEND(id));
      return response.data;
    } catch (err) {
      throw handleError(err, "Error syncing items to Intervend");
    }
  };

  getVendingController = async ({ id }) => {
    try {
      const response = await apiBase.get(GET_VENDING_CONTROLLER(id));
      if (response.status !== 200) throw new Error();
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error fetching vending controller");
    }
  };

  getLastSignedInvoice = async ({ id, captchaData }) => {
    try {
      const response = await apiBase.get(
        GET_VENDING_CONTROLLER_LAST_INVOICE(id, captchaData)
      );
      if (response.status !== 200) throw new Error();
      const { data, message } = response.data;
      return { data, message };
    } catch (err) {
      throw handleError(err, "Error fetching invoice");
    }
  };

  getVendingControllerInvoices = async ({
    id,
    page,
    perPage,
    term = " ",
    sortBy,
    order,
  }) => {
    try {
      const resposne = await apiBase.get(
        GET_VENDING_CONTROLLER_INVOICES(id, page, perPage, term, sortBy, order)
      );
      if (resposne.status !== 200) throw new Error();
      const { data } = resposne.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error fetching invoices");
    }
  };
 
  downloadXLs = async () => {
    try {
      const response = await apiBase.get(DOWNLOAD_VENDING_CONTROLLERS_XLS, {
        responseType: "blob",
      });
      const fileName = "VendingControllers.xlsx";
      fileDownload(response.data, fileName);
    } catch (err) {
      console.log("err ", err);
    }
  };

  downloadOtaExport = async (format = "xlsx") => {
    try {
      const response = await apiBase.get(DOWNLOAD_VENDING_CONTROLLERS_OTA(format), {
        responseType: "blob",
      });
      const fileName = `VendingControllers_OTA.${format}`;
      fileDownload(response.data, fileName);
    } catch (err) {
      console.log("err ", err);
      throw handleError(err, "Error downloading OTA export");
    }
  };
}

export default new VendingControllersService();
